import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { format } from 'date-fns'
import EmptyState from 'components/reusables/emptyState'
import { learningPathsService } from 'api/administration/learningPaths'
import { TableSkeleton } from 'components/reusables/skeletons'
import { ModalButtons } from 'components/reusables/modalButtons'
import { Modal } from 'components/reusables/modal'
import { ICourse } from 'interfaces/learningPath'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { slices } from 'store/slices'
import { CourseStateBadge } from 'components/reusables/states'
import { Wrapper, RankmiTableWrapper } from './styled'

interface IProps {
  collectionOrder: number
  close: () => void
}

const AddCourses = ({ collectionOrder, close }: IProps) => {
  const { t } = useTranslation()
  const prefix =
    'administration.learningPaths.manage.create.content.collections.courses.addCourse'
  const dispatch = useAppDispatch()
  const [courses, setCourses] = useState<ICourse[]>([])
  const [selectedCourses, setSelectedCourses] = useState<ICourse[]>([])
  const [loading, setLoading] = useState(false)
  const [searchCriteria, setSearchCriteria] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const configuration = useAppSelector(slices.learningPath.configuration)
  const content = useAppSelector(slices.learningPath.content)

  useEffect(() => {
    getCourses(currentPage, '')
  }, [])

  const getCourses = async (page: number, criteria: string) => {
    setLoading(true)
    await learningPathsService
      .getCourses(configuration.id, criteria, page)
      .then((response) => {
        removeSelectedCourses(response.courses)
        setTotalRecords(response.total)
      })
    setLoading(false)
  }

  const removeSelectedCourses = (newCourses: ICourse[]) => {
    setCourses(
      newCourses
        .filter((course) => {
          let selectedCourse = false
          outerLoop: for (const collection of content.collections) {
            for (const relatedCourse of collection.relatedCourses) {
              if (relatedCourse.course.id === course.id) {
                selectedCourse = true
                break outerLoop
              }
            }
          }
          return !selectedCourse
        })
        .map((course) => {
          return {
            ...course,
            key: course.id,
          }
        })
    )
  }

  const onAdd = () => {
    dispatch(
      slices.learningPath.addCourses({
        selectedCourses: selectedCourses,
        collectionOrder: collectionOrder,
      })
    )
    close()
  }

  const onSearch = (value: string) => {
    setSearchCriteria(value)
    setCurrentPage(1)
    getCourses(1, value)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
    getCourses(value, searchCriteria)
  }

  const onRowSelected = (value: any) => {
    if (value['dispatcher'] !== 'handleUnselectAllRowsOnFilter') {
      const courseList: ICourse[] = [
        ...selectedCourses,
        ...value['selectedRows'],
      ]
      const addedCourses: number[] = []
      setSelectedCourses(
        courseList.filter((course) => {
          if (
            value['selectedRowKeys'].includes(course.id) &&
            !addedCourses.includes(course.id)
          ) {
            addedCourses.push(course.id)
            return true
          }
          return false
        })
      )
    }
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.rowsCount`),
      rowsCount_plural: t(`${prefix}.rowsCountPlural`),
    },
  }

  const columns = [
    {
      title: t(`${prefix}.columns.name`),
      render: (data: ICourse) => data.name,
    },
    {
      title: t(`${prefix}.columns.createdAt`),
      width: 140,
      render: (data: ICourse) =>
        format(new Date(data.createdAt), 'dd-MM-yyyy kk:mm'),
    },
    {
      title: t(`${prefix}.columns.state`),
      width: 140,
      render: (data: ICourse) => <CourseStateBadge state={data.state} />,
    },
  ]

  return (
    <Modal
      title={t(`${prefix}.title`)}
      actions={
        <ModalButtons
          primaryText={t(`${prefix}.buttons.primary`)}
          primaryAction={onAdd}
          disablePrimaryButton={selectedCourses.length === 0}
          secondaryText={t(`${prefix}.buttons.secondary`)}
          secondaryAction={close}
        />
      }
      maxheight={500}
      maxwidth={800}
      close={close}
    >
      <Wrapper>
        {loading && courses.length === 0 ? (
          <TableSkeleton />
        ) : (
          <RankmiTableWrapper>
            <RankmiTable
              locale={locale}
              columnHeaders={columns}
              data={courses}
              total={totalRecords}
              search={searchCriteria}
              onSearch={onSearch}
              onRowSelected={onRowSelected}
              onPaginationChange={onPaginationChange}
              selectedRows={selectedCourses.map((row) => row.id)}
              currentPage={currentPage}
              countIcon={faBook}
              loading={loading}
              delaySearch={true}
              customEmpty={
                <EmptyState title={t(`${prefix}.emptyState`)} icon={faBook} />
              }
            />
          </RankmiTableWrapper>
        )}
      </Wrapper>
    </Modal>
  )
}

export default AddCourses
